import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  BookingService,
  SitterReview,
  SitterReviewService,
  SitterService,
  Sitter,
} from 'us-web-services';
import {
  DateFormat,
  formatDateFromString,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { SitterProfileChrome } from './components/SitterProfileChrome';

interface SortOption {
  key: string;
  nameKey: string;
  titleKey: string;
  sort: string;
  dirs: Record<string, 'ASC' | 'DESC'>;
}

const SORT_OPTIONS: SortOption[] = [
  { key: 'byRating', nameKey: 'pages.sitterReviews.sort.byRating.name', titleKey: 'pages.sitterReviews.sort.byRating.title', sort: 'vote,date', dirs: { vote: 'DESC', date: 'DESC' } },
  { key: 'byRatingLow', nameKey: 'pages.sitterReviews.sort.byRatingLow.name', titleKey: 'pages.sitterReviews.sort.byRatingLow.title', sort: 'vote', dirs: { vote: 'ASC' } },
  { key: 'byMostRecent', nameKey: 'pages.sitterReviews.sort.byMostRecent.name', titleKey: 'pages.sitterReviews.sort.byMostRecent.title', sort: 'date', dirs: { date: 'DESC' } },
  { key: 'byOldest', nameKey: 'pages.sitterReviews.sort.byOldest.name', titleKey: 'pages.sitterReviews.sort.byOldest.title', sort: 'date', dirs: { date: 'ASC' } },
];

const formatDate = (iso?: string): string =>
  iso ? formatDateFromString(iso, DateFormat.DATE_LONG_FORMAT_SLASHES) : '';

export const SitterReviews = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: sitterIdParam } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [sitter, setSitter] = useState<Sitter | null>(null);
  const [reviews, setReviews] = useState<SitterReview[]>([]);
  const [canReview, setCanReview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<string>('byRating');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [page, setPage] = useState<number>(Number(searchParams.get('page') || 1) || 1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalSize, setTotalSize] = useState<number>(0);

  const sitterId = Number(sitterIdParam);
  const sortOption = SORT_OPTIONS.find((o) => o.key === sortKey) ?? SORT_OPTIONS[0];
  const isOwnProfile = Boolean(user?.id && user.id === sitterId);
  const viewerIsParent = Boolean(user?.parent);

  useEffect(() => {
    document.title = 'UrbanSitter - Reviews';
  }, []);

  useEffect(() => {
    if (!sitterId) return;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, { params: { userId: user?.id, parentId: user?.id } });
        setSitter(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[SitterReviews] sitter load failed', err);
      }
    };
    void load();
  }, [sitterId, user?.id]);

  useEffect(() => {
    if (!sitterId || !user?.id) return;
    if (!viewerIsParent) {
      setCanReview(false);
      return;
    }
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.getByFilter({
          params: {
            limit: 1,
            sitterId,
            parentId: user.id,
            userId: user.id,
            statuses: ['closed_completed', 'parent_cancel', 'sitter_cancel'],
          },
        });
        setCanReview((resp.data?.meta?.size ?? 0) > 0);
      } catch {
        setCanReview(false);
      }
    };
    void load();
  }, [sitterId, user?.id, viewerIsParent]);

  useEffect(() => {
    if (!sitterId) return;
    setLoading(true);
    const sortDirs: Record<string, string> = {};
    Object.entries(sortOption.dirs).forEach(([k, v]) => { sortDirs[`${k}.dir`] = v; });
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterReviewService.getByFilter({
          params: {
            sort: sortOption.sort,
            page,
            sitterId,
            visibility: 'public',
            ...sortDirs,
          },
        });
        setReviews(resp.data?.data ?? []);
        const meta = resp.data?.meta;
        setTotalPages(meta?.pageCount ?? 1);
        setTotalSize(meta?.size ?? 0);
      } catch (err) {
        console.warn('[SitterReviews] load failed', err);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [sitterId, page, sortOption.key, sortOption.sort]);

  const headline = useMemo(() => {
    if (!sitter || !viewerIsParent) return null;
    const firstName = sitter.firstName ?? '';
    const reviewed = Boolean(sitter.relationship?.reviewed);
    if (canReview && reviewed) return { text: t('pages.sitterReviews.headings.thanksReviewed', { name: firstName }), cta: t('pages.sitterReviews.buttons.editReview') };
    if (canReview && !reviewed) return { text: t('pages.sitterReviews.headings.pleaseReview', { name: firstName }), cta: t('pages.sitterReviews.buttons.reviewSitter') };
    if (!canReview && reviewed) return { text: t('pages.sitterReviews.headings.thanksRecommended', { name: firstName }), cta: t('pages.sitterReviews.buttons.editReview') };
    return { text: t('pages.sitterReviews.headings.helpRecommend', { name: firstName }), cta: t('pages.sitterReviews.buttons.recommend') };
  }, [sitter, viewerIsParent, canReview, t]);

  const setPageReplace = (p: number): void => {
    setPage(p);
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set('page', String(p));
      return next;
    }, { replace: true });
  };

  return (
    <div className='container py-6 max-w-4xl mx-auto'>
      <SitterProfileChrome sitter={sitter} activeTab='reviews' viewerUserId={user?.id} />

      {headline && sitter && !isOwnProfile && (
        <div className='mb-4'>
          <h3 className='text-lg font-semibold'>{headline.text}</h3>
          <a className='inline-block mt-2 btn btn-secondary underline' href={`/app/sitter/${sitter.id}/review`}>
            {headline.cta}
          </a>
        </div>
      )}

      {loading && <p className='text-neutral-600'>{t('pages.sitterReviews.loading')}</p>}

      {!loading && totalSize > 0 && (
        <>
          <div className='flex items-center justify-between'>
            <h4 className='font-semibold'>{t(sortOption.titleKey)}</h4>
            <div className='relative'>
              <Button
                buttonType={ButtonType.tertiary}
                onClick={(): void => setSortMenuOpen((s) => !s)}
                className='px-2 py-1'
                aria-label={t('pages.sitterReviews.sort.ariaLabel')}
              >
                ▾
              </Button>
              {sortMenuOpen && (
                <ul className='absolute right-0 mt-1 bg-white border border-neutral-200 rounded shadow z-10 w-56'>
                  <li className='px-3 py-2 text-xs uppercase text-neutral-500'>
                    {t('pages.sitterReviews.sort.header')}
                  </li>
                  {SORT_OPTIONS.map((opt) => (
                    <li key={opt.key}>
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => { setSortKey(opt.key); setPageReplace(1); setSortMenuOpen(false); }}
                        className='w-full text-left px-3 py-2 hover:bg-neutral-100'
                      >
                        {sortKey === opt.key && '✓ '}
                        {t(opt.nameKey)}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <hr className='my-3' />

          <ul className='space-y-4'>
            {reviews.map((review) => (
              <li key={review.id} className='flex flex-col sm:flex-row gap-3 border-b border-neutral-200 pb-4'>
                <a href={`/app/parent/${review.parent?.id ?? ''}`} className='shrink-0'>
                  <img src={review.parent?.pictureUrl} alt='' className='w-20 h-20 rounded object-cover' />
                </a>
                <div className='flex-1'>
                  <div className='flex items-center gap-2'>
                    <div className='flex gap-0.5'>
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span key={i} className={i < (review.rating ?? 0) ? 'text-amber-400' : 'text-neutral-300'}>★</span>
                      ))}
                    </div>
                    <span className='text-sm font-semibold'>
                      {t('pages.sitterReviews.reviewedOn', { date: formatDate(review.date ?? review.created) })}
                      {review.updated && formatDate(review.updated) !== formatDate(review.date ?? review.created) && (
                        <span className='font-normal text-neutral-600'>
                          {' '}{t('pages.sitterReviews.editedOn', { date: formatDate(review.updated) })}
                        </span>
                      )}
                    </span>
                  </div>
                  {review.title && <div className='font-semibold mt-1'>{review.title}</div>}
                  {review.comment && <p className='mt-1'>{review.comment}</p>}
                  {!review.reply && isOwnProfile && (
                    <Button
                      buttonType={ButtonType.secondary}
                      className='mt-2'
                      onClick={(): void => navigate(`/sitter/${sitterId}/reviews/${review.id}`)}
                    >
                      {t('pages.sitterReviews.reply')}
                    </Button>
                  )}
                  {review.reply && (
                    <div className='bg-neutral-100 rounded p-3 mt-3'>
                      <div className='flex items-start gap-3'>
                        <img src={sitter?.pictureUrl} alt='' className='w-14 h-14 rounded object-cover' />
                        <div className='flex-1'>
                          <h5 className='font-semibold'>
                            {review.replyDate || review.updated
                              ? t('pages.sitterReviews.responseOn', {
                                  name: sitter?.firstName ?? '',
                                  date: formatDate(review.replyDate || review.updated),
                                })
                              : t('pages.sitterReviews.response', {
                                  name: sitter?.firstName ?? '',
                                })}
                          </h5>
                          <p>{review.reply}</p>
                          {isOwnProfile && (
                            <Button
                              buttonType={ButtonType.secondary}
                              className='mt-2'
                              onClick={(): void => navigate(`/sitter/${sitterId}/reviews/${review.id}`)}
                            >
                              {t('pages.sitterReviews.edit')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <div className='flex justify-center items-center gap-4 mt-6'>
              <Button buttonType={ButtonType.secondary} onClick={(): void => setPageReplace(page - 1)} disabled={page <= 1}>
                {t('common.previous')}
              </Button>
              <span className='text-sm'>{t('pages.sitterReviews.pageOf', { current: page, total: totalPages })}</span>
              <Button buttonType={ButtonType.secondary} onClick={(): void => setPageReplace(page + 1)} disabled={page >= totalPages}>
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      )}

      {!loading && totalSize === 0 && sitter && (
        <h3 className='text-lg font-semibold'>
          {t('pages.sitterReviews.empty', { name: sitter.firstName ?? '' })}
        </h3>
      )}
    </div>
  );
});
